import { Box, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, useColorModeValue } from '@chakra-ui/react';
import { useMemo } from 'react';
import { translate } from '../../i18n';
import type { CourtArc } from './useCourtPulseData';

// ---------------------------------------------------------------------------
// Court Pulse Expanded — full-screen radial view.
// One ring per court, time running clockwise (00:00 at top).
// Each booked slot is drawn at its actual time position on the 24h clock.
// ---------------------------------------------------------------------------

interface CourtPulseExpandedProps {
	isOpen: boolean;
	onClose: () => void;
	arcs: CourtArc[];
	totalOccupancy: number;
	/** Day start (00:00) — needed to convert booked ranges to clock angles */
	windowStart: Date;
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
	const span = Math.min(endDeg - startDeg, 359.99);
	if (span < 0.1) return ''; // too small to draw
	const endClamped = startDeg + span;
	const startRad = ((startDeg - 90) * Math.PI) / 180;
	const endRad = ((endClamped - 90) * Math.PI) / 180;
	const x1 = cx + r * Math.cos(startRad);
	const y1 = cy + r * Math.sin(startRad);
	const x2 = cx + r * Math.cos(endRad);
	const y2 = cy + r * Math.sin(endRad);
	const largeArc = span > 180 ? 1 : 0;
	return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

/** Convert a timestamp to degrees on the 24h clock (00:00 = 0deg = top) */
function timestampToDeg(ts: number, dayStart: number): number {
	const msIntoDay = ts - dayStart;
	const msInDay = 24 * 60 * 60 * 1000;
	return (msIntoDay / msInDay) * 360;
}

export default function CourtPulseExpanded({ isOpen, onClose, arcs, totalOccupancy, windowStart }: CourtPulseExpandedProps) {
	const brand = useColorModeValue('#023F7F', '#3578BF');
	const ghost = useColorModeValue('rgba(44, 42, 38, 0.08)', 'rgba(255, 255, 255, 0.08)');
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const mutedColor = useColorModeValue('secondaryGray.600', 'gray.400');

	const svgSize = 400;
	const center = svgSize / 2;
	const maxRadius = center - 40;
	const ringWidth = Math.max(4, Math.min(12, (maxRadius - 30) / Math.max(arcs.length, 1)));
	const ringGap = Math.max(2, ringWidth * 0.3);

	const dayStartMs = windowStart.getTime();

	// Time markers around the clock
	const timeLabels = useMemo(() => {
		const labels = [];
		for (let h = 0; h < 24; h += 3) {
			const angle = (h / 24) * 360;
			const rad = ((angle - 90) * Math.PI) / 180;
			const r = maxRadius + 24;
			const x = center + r * Math.cos(rad);
			const y = center + r * Math.sin(rad);
			labels.push({ hour: h, x, y });
		}
		return labels;
	}, [center, maxRadius]);

	// Current time indicator
	const now = new Date();
	const currentHourAngle = ((now.getHours() + now.getMinutes() / 60) / 24) * 360;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size='xl' isCentered>
			<ModalOverlay bg='blackAlpha.400' backdropFilter='blur(4px)' />
			<ModalContent borderRadius='20px' overflow='hidden'>
				<ModalHeader fontFamily='"Urbanist", "Figtree", sans-serif' fontWeight='700'>
					{translate('court_pulse.venue_pulse')} — {Math.round(totalOccupancy * 100)}% {translate('court_pulse.occupied')}
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb='24px'>
					<Flex justify='center' mb='16px'>
						<svg
							width={svgSize}
							height={svgSize}
							viewBox={`0 0 ${svgSize} ${svgSize}`}
							fill='none'
						>
							{/* Time labels */}
							{timeLabels.map(({ hour, x, y }) => (
								<text
									key={hour}
									x={x}
									y={y}
									textAnchor='middle'
									dominantBaseline='central'
									fill='currentColor'
									fontSize='11'
									fontFamily='"JetBrains Mono", monospace'
									opacity={0.5}
								>
									{String(hour).padStart(2, '0')}:00
								</text>
							))}

							{/* Current time indicator */}
							{(() => {
								const rad = ((currentHourAngle - 90) * Math.PI) / 180;
								const innerR = maxRadius - arcs.length * (ringWidth + ringGap) - 8;
								const outerR = maxRadius + 8;
								const x1 = center + Math.max(innerR, 10) * Math.cos(rad);
								const y1 = center + Math.max(innerR, 10) * Math.sin(rad);
								const x2 = center + outerR * Math.cos(rad);
								const y2 = center + outerR * Math.sin(rad);
								return (
									<line
										x1={x1} y1={y1} x2={x2} y2={y2}
										stroke={brand}
										strokeWidth='2'
										strokeDasharray='4 4'
										opacity={0.6}
									/>
								);
							})()}

							{/* Court rings — each booked range drawn at its actual time position */}
							{arcs.map((arc, idx) => {
								const r = maxRadius - idx * (ringWidth + ringGap);
								if (r < 20) return null;

								const ghostPath = describeArc(center, center, r, 0, 359.99);

								return (
									<g key={arc.courtId}>
										{/* Ghost ring (full 24h circle) */}
										<path
											d={ghostPath}
											stroke={ghost}
											strokeWidth={ringWidth}
											strokeLinecap='round'
											fill='none'
										/>
										{/* Individual booked ranges at their actual time positions */}
										{arc.bookedRanges.map((range, rangeIdx) => {
											const startDeg = timestampToDeg(range.start, dayStartMs);
											const endDeg = timestampToDeg(range.end, dayStartMs);
											const path = describeArc(center, center, r, startDeg, endDeg);
											if (!path) return null;
											return (
												<path
													key={rangeIdx}
													d={path}
													stroke={brand}
													strokeWidth={ringWidth}
													strokeLinecap='round'
													fill='none'
													opacity={arc.isLive ? 1 : 0.7}
												/>
											);
										})}
									</g>
								);
							})}
						</svg>
					</Flex>

					{/* Legend */}
					<Flex direction='column' gap='8px' px='16px'>
						{arcs.map((arc) => (
							<Flex key={arc.courtId} align='center' gap='12px'>
								<Box
									w='12px' h='12px' borderRadius='full'
									bg={brand} opacity={arc.occupancy > 0 ? 1 : 0.2}
								/>
								<Text fontSize='sm' fontWeight='500' flex='1'>
									{arc.courtName}
								</Text>
								<Text
									fontSize='sm'
									fontFamily='var(--mono-font, inherit)'
									color={mutedColor}
								>
									{Math.round(arc.occupancy * 100)}%
									{arc.isLive && (
										<Text as='span' color={brand} ml='4px'>●</Text>
									)}
								</Text>
							</Flex>
						))}
					</Flex>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
